import type { Dispatch, SetStateAction } from 'react';

import type { CountryAiMode, CountryGenResult } from './multiCountryBulkEdit.types';
import { generatePerDrop, type GenerationProgress } from './perDropGeneration';

export interface SenderGenMutationLike {
  mutateAsync: (p: { groupId: string; countryKey: string; amount: number }) => Promise<{ senders: string[] }>;
}

export async function runMultiCountrySenderGeneration(params: {
  senderMode: CountryAiMode;
  countryKeys: string[];
  globalCountryPerDrop: string[];
  groupId: string;
  amount: number;
  groupIdsPerDrop?: string[];
  genSenders: SenderGenMutationLike;
  setProgress: (p: GenerationProgress | null) => void;
  setResultsByCountry: Dispatch<SetStateAction<Record<string, CountryGenResult>>>;
  onSendersPerDropMergeOrder?: (batches: string[][]) => void;
  onSendersGenerated?: (byCountry: Record<string, string[]>) => void;
}): Promise<void> {
  const {
    senderMode,
    countryKeys,
    globalCountryPerDrop,
    groupId,
    amount,
    groupIdsPerDrop,
    genSenders,
    setProgress,
    setResultsByCountry,
    onSendersPerDropMergeOrder,
    onSendersGenerated,
  } = params;
  const collected: Record<string, string[]> = {};
  if (senderMode === 'per_drop') {
    await generatePerDrop({
      countryKeys,
      globalCountryPerDrop,
      groupId,
      amount,
      groupIdsPerDrop,
      genFn: (gId, ck, amt) => genSenders.mutateAsync({ groupId: gId, countryKey: ck, amount: amt }),
      extractResult: (d) => d.senders,
      setProgress,
      onMergeOrderBatches: onSendersPerDropMergeOrder,
      applyResult: (ck, batches, gIds, _translationBatches) => {
        void _translationBatches;
        const flat = batches.flat();
        collected[ck] = flat;
        setResultsByCountry((prev) => ({
          ...prev,
          [ck]: {
            ...(prev[ck] ?? { countryKey: ck, texts: [] }),
            countryKey: ck,
            senders: flat,
            senderBatches: batches,
            groupIdsPerDrop: gIds,
          },
        }));
      },
    });
    return;
  }
  if (senderMode === 'same_all') {
    setProgress({ current: 0, total: 1, currentCountry: countryKeys[0] ?? '' });
    const data = await genSenders.mutateAsync({ groupId, countryKey: countryKeys[0] ?? '', amount });
    setResultsByCountry((prev) => {
      const next = { ...prev };
      for (const ck of countryKeys) {
        next[ck] = { ...(next[ck] ?? { countryKey: ck, texts: [] }), countryKey: ck, senders: data.senders };
        collected[ck] = data.senders;
      }
      return next;
    });
    setProgress({ current: 1, total: 1, currentCountry: '' });
    onSendersGenerated?.(collected);
    return;
  }
  for (let i = 0; i < countryKeys.length; i++) {
    const ck = countryKeys[i]!;
    const data = await genSenders.mutateAsync({ groupId, countryKey: ck, amount });
    collected[ck] = data.senders;
    setResultsByCountry((prev) => ({
      ...prev,
      [ck]: { ...(prev[ck] ?? { countryKey: ck, texts: [] }), countryKey: ck, senders: data.senders },
    }));
    setProgress({ current: i + 1, total: countryKeys.length, currentCountry: ck });
  }
  onSendersGenerated?.(collected);
}
